import { useEffect, useRef } from 'react';
import { HttpStatusCode } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from 'shared/src/api/httpClient';
import { queryKey } from 'shared/src/constant';
import {
  bridge,
  isWeb,
  sanitizeRedirectPath,
  setMemberToken,
  useNavigator,
} from 'shared/src/utils';
import { usePostAppleLogin } from '../queries/usePostAppleLogin';
import { usePostFacebookLogin } from '../queries/usePostFacebookLogin';
import { usePostKakaoLogin } from '../queries/usePostKakaoLogin';
import { usePostNaverLogin } from '../queries/usePostNaverLogin';
import { usePostTermsAgreementVerify } from '../queries/usePostTermsAgreementVerify';
import {
  usePendingSocialAuth,
  useSetAlreadyRegistered,
} from '../../../socialAuthHandoff';
import { REGISTER_STATUS_CODE } from '../../../registerStatusCode';
import { useRegisterCompleteBottomSheet } from '../components/RegisterComplete/useRegisterCompleteBottomSheet';
import { useRegisterEmailBottomSheet } from '../components/RegisterEmail/useRegisterEmailBottomSheet';
import { useRegisterDrawer } from '../components/RegisterDrawer/useRegisterDrawer';
import { useLogCompleteSocialLogin } from './useLogCompleteSocialLogin';

/**
 * 소셜 OAuth 콜백 처리 — 2단계: 확보된 provider 토큰으로 자사 JWT 발급 (웹 전용).
 *
 * AuthCallback이 전역 아톰에 담아 넘긴 provider 토큰을 마운트 시 소비해
 * JWT를 발급·저장하고 복귀 경로(URL ?redirect=)로 이동한 뒤 아톰을 비운다.
 */
export const useConsumePendingSocialAuth = () => {
  const [pending, setPending] = usePendingSocialAuth();
  const { mutateAsync: verifyTermsAgreement } = usePostTermsAgreementVerify();
  const { mutateAsync: postKakaoLogin } = usePostKakaoLogin();
  const { mutateAsync: postNaverLogin } = usePostNaverLogin();
  const { mutateAsync: postFacebookLogin } = usePostFacebookLogin();
  const { mutateAsync: postAppleLogin } = usePostAppleLogin();
  const setAlreadyRegistered = useSetAlreadyRegistered();
  const { show: showRegisterComplete } = useRegisterCompleteBottomSheet();
  const { show: showRegisterEmail } = useRegisterEmailBottomSheet();
  const { show: showRegisterDrawer } = useRegisterDrawer();
  const { navigator } = useNavigator();
  const queryClient = useQueryClient();
  const logCompleteSocialLogin = useLogCompleteSocialLogin();
  // effect 중복 실행(StrictMode 등)에서 재발급을 막는다.
  const handledRef = useRef(false);

  useEffect(() => {
    if (!isWeb || pending == null || handledRef.current) return;
    handledRef.current = true;
    const { provider, accessToken } = pending;
    // 캡처 즉시 아톰을 비운다. 이후 로직은 로컬 provider/accessToken으로 진행하므로,
    // 드로어가 열려 있는 동안 Login이 언마운트·재마운트돼도 중복 발급/드로어가 생기지 않는다.
    setPending(null);

    const isKakaoApple = provider === 'kakao' || provider === 'apple';

    // 보완 필드(email 등)를 실어 같은 provider 로그인을 (재)발급한다.
    const issueLogin = (extra?: {
      email?: string;
      phone_number?: string;
      birth_date?: string;
    }) => {
      switch (provider) {
        case 'kakao':
          return postKakaoLogin({ access_token: accessToken, ...extra });
        case 'naver':
          return postNaverLogin({ access_token: accessToken, ...extra });
        case 'facebook':
          return postFacebookLogin({ access_token: accessToken, ...extra });
        case 'apple':
          return postAppleLogin({
            id_token: accessToken,
            policy_approvement: true,
            ...extra,
          });
      }
    };

    const doRedirect = () => {
      const redirect = new URLSearchParams(window.location.search).get(
        'redirect',
      );
      // 외부 조작 가능한 값이므로 내부 경로만 허용(오픈 리다이렉트 방지).
      window.location.replace(sanitizeRedirectPath(redirect));
    };

    // JWT 발급 성공 후처리: 토큰 저장 → 신규(카카오/애플)면 가입완료 드로어 → 복귀.
    const completeLogin = async (
      result: Awaited<ReturnType<typeof issueLogin>>,
    ) => {
      await setMemberToken(result.token);
      logCompleteSocialLogin({ isNew: result.is_new, type: provider });
      // 로그인 직후 회원 정보(myInfo)를 무효화해 로그인 상태가 UI에 즉시 반영되게 한다.
      // 신규 소셜가입은 가입완료 드로어 때문에 리로드(window.location.replace)가
      // 지연되므로, 그 전에 anonymous myInfo를 무효화하지 않으면 로그아웃처럼 보인다.
      queryClient.invalidateQueries({ queryKey: queryKey.myInfo });
      if (result.is_new && isKakaoApple) {
        showRegisterComplete({
          isShowBirthdate: !result.is_birthday_exist,
        }).then(doRedirect, doRedirect);
      } else {
        doRedirect();
      }
    };

    (async () => {
      try {
        // 기가입 게이트: JWT 발급 전에 terms verify로 "다른 채널로 이미 가입한
        // 계정"이 있는지 확인한다. 있으면 발급을 중단하고 그 채널로 재로그인하도록
        // 안내한다.
        // verify 실패는 로그인을 막지 않는다(fail-open) — 실제 중복은 JWT 발급의
        // 409(AlreadyRegistered)가 백스톱이므로, 게이트 장애가 로그인을 끊지 않게 한다.
        const gate = await verifyTermsAgreement({
          provider,
          accessToken,
        }).catch(() => null);
        if (gate?.registered_member != null) {
          // 다른 채널로 이미 가입 → 그 채널로 재로그인하도록 안내 화면으로 이동.
          // channel/email(PII)은 URL 노출을 피해 아톰으로, redirect만 URL로 유지해
          // 재로그인 시 원래 복귀 경로가 살아있게 한다.
          const { channel, email, channels } = gate.registered_member;
          setAlreadyRegistered({
            channel,
            email,
            hasMultiLoginType: (channels?.length ?? 0) > 1,
          });
          const redirect =
            new URLSearchParams(window.location.search).get('redirect') ??
            undefined;
          navigator.moveToAlreadyRegistered(redirect);
          return;
        }

        await completeLogin(await issueLogin());
      } catch (error) {
        // 가입 보완 분기(D).
        if (error instanceof ApiError) {
          // 백스톱: 게이트(fail-open)가 놓친 기가입(409). 상세정보가 없어 안내만.
          if (
            error.status === HttpStatusCode.Conflict &&
            error.code === REGISTER_STATUS_CODE.AlreadyRegistered
          ) {
            bridge.toast(
              '이미 가입된 계정이 있어요. 기존 로그인 수단으로 로그인해 주세요.',
            );
            return;
          }
          if (error.status === HttpStatusCode.BadRequest) {
            if (isKakaoApple && error.code === REGISTER_STATUS_CODE.NeedPhone) {
              // 번호 점유 — 카카오/애플은 드로어 없이 안내 토스트로 차단.
              bridge.toast(
                '이미 가입된 휴대폰 번호입니다. 아이디 찾기를 이용해주세요.',
              );
              return;
            }
            if (isKakaoApple && error.code === REGISTER_STATUS_CODE.NeedEmail) {
              // 이메일 미제공 — 이메일만 입력받아 같은 로그인을 재제출한다.
              // 재제출 성공 후 드로어가 닫힌 뒤 completeLogin을 실행해 드로어 중첩을 피한다.
              let resubmitResult: Awaited<
                ReturnType<typeof issueLogin>
              > | null = null;
              await showRegisterEmail({
                onSubmit: async email => {
                  try {
                    resubmitResult = await issueLogin({ email });
                  } catch (resubmitError) {
                    bridge.toast(
                      resubmitError instanceof ApiError && resubmitError.message
                        ? resubmitError.message
                        : '로그인에 실패했어요',
                    );
                    throw resubmitError; // 실패 시 드로어 유지
                  }
                },
              });
              if (resubmitResult != null) {
                await completeLogin(resubmitResult);
              }
              return;
            }
            if (
              !isKakaoApple &&
              (error.code === REGISTER_STATUS_CODE.NeedPhone ||
                error.code === REGISTER_STATUS_CODE.NeedEmail)
            ) {
              // 네이버/페북: 보완 필드(휴대폰 SMS·생일·약관, NeedEmail이면 이메일까지)를
              // 입력받아 같은 로그인을 재제출한다. 드로어가 닫힌 뒤 completeLogin 실행.
              let resubmitResult: Awaited<
                ReturnType<typeof issueLogin>
              > | null = null;
              await showRegisterDrawer({
                isShowEmail: error.code === REGISTER_STATUS_CODE.NeedEmail,
                isShowMobile: true,
                isShowBirthdate: true,
                isShowTerms: true,
                onSubmit: async fields => {
                  try {
                    resubmitResult = await issueLogin(fields);
                  } catch (resubmitError) {
                    bridge.toast(
                      resubmitError instanceof ApiError && resubmitError.message
                        ? resubmitError.message
                        : '로그인에 실패했어요',
                    );
                    throw resubmitError; // 실패 시 드로어 유지
                  }
                },
              });
              if (resubmitResult != null) {
                await completeLogin(resubmitResult);
              }
              return;
            }
          }
        }
        bridge.toast(
          error instanceof ApiError && error.message
            ? error.message
            : '로그인에 실패했어요',
        );
      }
    })();
  }, [
    logCompleteSocialLogin,
    navigator,
    pending,
    postAppleLogin,
    postFacebookLogin,
    postKakaoLogin,
    postNaverLogin,
    queryClient,
    setAlreadyRegistered,
    setPending,
    showRegisterComplete,
    showRegisterDrawer,
    showRegisterEmail,
    verifyTermsAgreement,
  ]);
};
